import {
  ConfigurableProduct,
  ConfigurableVariant,
  ProductInterface,
  usePdpQuery,
  useProductSizesQuery,
} from '@magentoTypes'
import clsx from 'clsx'
import { useTranslation } from 'next-i18next'
import { useEffect, useState } from 'react'
import { useForm, SubmitHandler } from 'react-hook-form'
import { Image } from '@next/image'
import { useQueryClient } from '@tanstack/react-query'
import { createMagentoClient } from '~/graphql/magentoClient'
import { useRouter } from 'next/router'
import { ErrorMessage } from '@hookform/error-message'
import { FormIcon, Icons } from '~/elements/Icons/FormIcon'
import { useProductAttributes } from '~/hooks/useProductAttributes'
import { Price } from './Price'
import { filteringByForm } from '../lib/filteringByForm'
import { availableForms } from '../lib/availableForms'
import { matchUrlToForm } from '../lib/matchUrlToForm'
import { DEFAULT_LOCALE } from '~/config/constants'
import { createLanguageRegionLocale } from '~/lib/createLanguageRegionLocale'
import { useDevice } from '~/hooks/useDevice'
import { StockNotificationPopup } from './StockNotificationPopup'
import { useRebates } from '~/hooks/useRebates'
import { ButtonMain } from '~/elements/Button/ButtonMain'
type Props = {
  closeFlyout: () => void
  showProductSelector: boolean
}
type Inputs = {
  color: string
  form: string
  size: number
}
const samplesValueIndex = [
  11932, // 15x15cm - Live/Staging
  10771, // 10x10cm - Live/Staging
]
export const ProductSelector = ({ closeFlyout, showProductSelector }: Props) => {
  const router = useRouter()
  const queryClient = useQueryClient()
  const finalLocale = router.locale ? createLanguageRegionLocale(router.locale) : DEFAULT_LOCALE
  const magentoClient = createMagentoClient({ usePost: true, queryClient, locale: finalLocale })
  const { data: pdpData } = usePdpQuery(
    magentoClient,
    { urlKey: router?.query?.['url_key'] as string },
    { enabled: !!router?.query?.['url_key'] },
  )
  const product = pdpData?.products?.items?.[0] as ConfigurableProduct
  const [hasScrolled, setHasScrolled] = useState(false)
  const [routeChanging, setRouteChanging] = useState(false)

  const [currentForm, setCurrentForm] = useState(product?.benuta_form_new)
  const [currentUrlKey, setCurrentUrlKey] = useState(product?.url_key)
  const { t } = useTranslation()

  const { findLabel } = useProductAttributes()
  const { calculateDiscount } = useRebates()

  const filteredByForm = filteringByForm({ product, currentForm })

  const { register, handleSubmit, watch, formState, getValues } = useForm<Inputs>({
    defaultValues: {
      color: filteredByForm?.[0]?.url_key as string,
      form: product?.benuta_form_new?.toString(),
    },
  })

  const { data } = useProductSizesQuery(
    magentoClient,
    { urlKey: currentUrlKey as string },
    { enabled: !!currentUrlKey, keepPreviousData: true, staleTime: 0 },
  )
  useEffect(() => {
    const handleRouteStart = () => {
      setRouteChanging(true)
    }
    const handleRouteComplete = () => {
      setRouteChanging(false)
    }

    router.events.on('routeChangeStart', handleRouteStart)
    router.events.on('routeChangeComplete', handleRouteComplete)

    return () => {
      router.events.off('routeChangeStart', handleRouteStart)
      router.events.off('routeChangeComplete', handleRouteComplete)
    }
  }, [router.events])
  const onSubmit: SubmitHandler<Inputs> = async (data) => {
    try {
      if (useDevice.IS_ANDROID) {
        window.location.replace(
          `/${data.color}.html?selected_size=${data.size.toString()}#productvariations`,
        )
      } else {
        router.query.slug = `${data.color}.html`
        router.query['selected_size'] = data.size.toString()

        await router.push({
          pathname: router.pathname,
          query: router.query,
          hash: 'productvariations',
        })

        closeFlyout()
      }
    } catch (e) {
      console.error(e)
    }
  }

  const sorted = (variants: ConfigurableVariant[]) => {
    if (!variants) return []
    const sorted = variants?.sort((a, b) => {
      if (
        a?.product?.price_range?.minimum_price?.regular_price?.value &&
        b?.product?.price_range?.minimum_price?.regular_price?.value
      ) {
        return (
          a?.product?.price_range?.minimum_price?.regular_price?.value -
          b?.product?.price_range?.minimum_price?.regular_price?.value
        )
      } else return 0
    })
    return sorted
  }

  const [popupSku, setPopupSku] = useState('')
  const [showPopup, setShowPopup] = useState(false)
  const closePopup = () => setShowPopup(false)
  const openStockPopup = (sku: string) => {
    setPopupSku(sku)
    setShowPopup(true)
  }

  return (
    <div className="grid w-full grid-cols-1 lg:w-[550px]">
      <form
        className="vh100 relative grid grid-cols-1 grid-rows-[1fr_auto] bg-primary-white lg:w-[550px]"
        onSubmit={handleSubmit(onSubmit)}
      >
        <div
          onScroll={(e) => setHasScrolled((e.target as Element).scrollTop > 0)}
          className="relative h-full overflow-auto px-4  lg:px-8"
        >
          <div
            className={clsx(
              'pointer-events-none fixed top-[0px] right-0 z-50 h-[72px] w-full bg-primary-white bg-transparent lg:h-[104px] lg:w-[550px]',
              hasScrolled && 'shadow-[0px_4px_24px_rgba(0,0,0,0.15)]',
            )}
          ></div>
          <div className="relative pb-2">
            <p className="sticky top-0 z-10 -mx-4 bg-primary-white py-6 px-4 text-h4-mobile font-medium lg:-mx-8 lg:py-[38px] lg:px-8 lg:text-h4-desktop">
              {t('Choose Color')}
            </p>
            <menu
              className={clsx(
                'mt-6 mb-1 grid grid-cols-auto-fill-45 place-items-center gap-6 pl-1 transition',
              )}
              style={routeChanging ? { opacity: 0.5, pointerEvents: 'none' } : {}}
            >
              {(filteredByForm as ProductInterface[])?.map((item, i) => (
                <li key={item?.url_key}>
                  <label className="cursor-pointer">
                    <input
                      className="peer sr-only"
                      type="radio"
                      {...register('color', {
                        required: true,
                        onChange: async (e) => {
                          setCurrentUrlKey(e.target.value)
                          const size = router.query['selected_size']
                            ? `&selected_size=${router.query['selected_size']}`
                            : ''
                          await router.push(
                            `/${e.target.value}.html?url_key=${router.query['url_key']}${size}`,
                          )
                        },
                      })}
                      value={item?.url_key as string}
                      defaultChecked={i === 0}
                    />
                    <div className="grid place-content-center overflow-hidden outline-offset-[12px] outline-primary-black peer-checked:scale-75 peer-checked:outline peer-checked:outline-1">
                      <Image
                        src={item?.small_image?.url as string}
                        alt={item?.small_image?.label as string}
                        width={45}
                        height={60}
                      />
                    </div>
                  </label>
                </li>
              ))}
            </menu>
          </div>
          <div className="relative pb-2">
            <p className="sticky top-0 -mx-4 bg-primary-white px-4 py-6 text-h4-mobile font-medium lg:-mx-8 lg:px-8 lg:py-[38px] lg:text-h4-desktop">
              {t('Choose Form')}
            </p>
            <menu className="grid grid-cols-4 md:grid-cols-10 lg:grid-cols-6">
              {availableForms(product)?.map((item, i) => (
                <li key={`${item} ${i}`}>
                  <label className="cursor-pointer" htmlFor={item}>
                    <input
                      className="peer sr-only"
                      type="radio"
                      {...register('form', {
                        required: true,
                        onChange: (e) => {
                          setCurrentForm(Number(e.target.value))
                          setCurrentUrlKey(
                            matchUrlToForm({ product, formCode: Number(e.target.value) }),
                          )
                        },
                      })}
                      value={item}
                      id={item}
                    />
                    <div className="mb-2 flex h-[52px] w-[73px] items-center justify-center border border-grey-border peer-checked:border-primary-black">
                      <FormIcon name={item as Icons} />
                    </div>
                    <p className="w-[73px] text-center text-p-small text-grey-dark peer-checked:text-primary-black">
                      {findLabel({
                        attributeCode: 'benuta_form_new',
                        attributeValue: item,
                      })}
                    </p>
                  </label>
                </li>
              ))}
            </menu>
          </div>
          <div className="relative pb-2">
            <p className="sticky top-0 -mx-4 bg-primary-white px-4 py-6 text-h4-mobile font-medium lg:-mx-8 lg:px-8 lg:py-[38px] lg:text-h4-desktop">
              {t('Choose Size')}
            </p>
            <menu className="mb-28 grid grid-flow-row gap-4">
              {sorted(
                (data?.products?.items?.[0] as ConfigurableProduct)
                  ?.variants as ConfigurableVariant[],
              )?.map((item) => {
                const isSoldOut = item?.product?.stock_status === 'OUT_OF_STOCK'
                if (!samplesValueIndex.includes(item?.attributes?.[0]?.value_index as number)) {
                  const getDiscountAmount = () => {
                    return calculateDiscount(
                      item?.product?.price_range?.minimum_price?.regular_price?.value as number,
                      item?.product?.price_range?.minimum_price?.final_price?.value as number,
                    )
                  }
                  return (
                    <li key={item?.product?.sku}>
                      <label
                        className="cursor-pointer"
                        htmlFor={item?.product?.sku as string}
                        onClick={() => {
                          if (isSoldOut) openStockPopup(item?.product?.sku as string)
                        }}
                      >
                        <input
                          className="peer sr-only"
                          type="radio"
                          {...register('size', {
                            required: t('Please select a size'),
                          })}
                          disabled={isSoldOut}
                          value={item?.attributes?.[0]?.value_index as number}
                          id={item?.product?.sku as string}
                        />
                        <div
                          className={clsx(
                            'grid items-center rounded-[5px] border border-grey-border py-4 px-6 peer-checked:border-primary-black',
                            isSoldOut && 'text-[#CACACA]',
                            !isSoldOut && getDiscountAmount() > 0 ? 'grid-cols-3' : 'grid-cols-2',
                          )}
                        >
                          <span>
                            <span className="font-medium">
                              {item?.attributes?.[0]?.label}{' '}
                              {isSoldOut && `- ${t('Available soon')}`}
                            </span>{' '}
                            <br />{' '}
                            {isSoldOut ? (
                              <span className="text-p-small !text-primary-black underline underline-offset-4">
                                {t('Notify me')}
                              </span>
                            ) : (
                              <span className="text-p-small text-[#3EB489]">{t('In stock')}</span>
                            )}
                          </span>
                          {!isSoldOut && getDiscountAmount() > 0 && (
                            <span className="flex w-fit items-center justify-center justify-self-center bg-[#141414] px-3 text-[14px] text-primary-white">
                              -{getDiscountAmount()}%
                            </span>
                          )}
                          <span
                            className={clsx(
                              isSoldOut && 'justify-self-end text-grey-dark',
                              'flex gap-2 lg:gap-0',
                            )}
                          >
                            <Price
                              priceRange={item?.product?.price_range}
                              regPriceClass={clsx('w-full text-right', {
                                'text-[#CACACA]': isSoldOut,
                              })}
                              hideSalePrice={isSoldOut}
                              productSelectorFormat
                            />
                          </span>
                        </div>
                      </label>
                    </li>
                  )
                }
              })}
            </menu>
          </div>
        </div>
        <div
          className="absolute left-0 right-0 bottom-0 p-8"
          style={{
            background:
              'linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 1) 40%)',
          }}
        >
          <ErrorMessage
            errors={formState.errors}
            name="size"
            render={({ message }) => (
              <p className="py-4 text-center text-p text-primary-pink">{t(message)}</p>
            )}
          />
          <ButtonMain fullWidth type="submit">
            {t('Apply selection')}
          </ButtonMain>
        </div>
      </form>
      <StockNotificationPopup sku={popupSku} open={showPopup} close={closePopup} />
    </div>
  )
}
